/**
 * Payment Amount Input Component
 * Form for entering payment amount with validation
 */

import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useNavigate } from 'react-router-dom';
import { paymentAmountSchema } from '@/utils/validationSchemas';
import { usePaymentStore } from '@/store/paymentStore';
import { Button, Input, Card, CardHeader, CardTitle, CardBody } from '@/components/common';
import { formatCurrency } from '@/utils/formatters';
import { ROUTE_PATHS } from '@/utils/constants';

interface PaymentAmountInputProps {
  balance: number;
  minimumPayment: number;
  defaultAmount?: number;
  onBack?: () => void;
}

// Form data type before transformation (string)
interface PaymentFormInput {
  amount: string;
}

// Form data type after transformation (number)
interface PaymentFormOutput {
  amount: number;
}

export default function PaymentAmountInput({
  balance,
  minimumPayment,
  defaultAmount,
  onBack,
}: PaymentAmountInputProps) {
  const navigate = useNavigate();
  const setPaymentAmount = usePaymentStore((state) => state.setPaymentAmount);
  const [showFullPaymentInfo, setShowFullPaymentInfo] = useState(false);

  // Check if balance is zero or credit
  const hasNoBalanceDue = balance <= 0;

  const {
    register,
    handleSubmit,
    setValue,
    formState: { errors },
  } = useForm<PaymentFormInput, unknown, PaymentFormOutput>({
    resolver: zodResolver(paymentAmountSchema),
    mode: 'onBlur',
    defaultValues: {
      amount: defaultAmount ? defaultAmount.toFixed(2) : '',
    },
  });

  const onSubmit = (data: PaymentFormOutput) => {
    // Store payment amount in payment store
    setPaymentAmount(data.amount);

    // Navigate to confirmation page
    navigate(ROUTE_PATHS.CONFIRMATION);
  };

  const handlePayInFull = () => {
    setValue('amount', balance.toString(), { shouldValidate: true });
    setShowFullPaymentInfo(true);
  };

  const handlePayMinimum = () => {
    setValue('amount', minimumPayment.toString(), { shouldValidate: true });
    setShowFullPaymentInfo(false);
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>Payment Amount</CardTitle>
      </CardHeader>
      <CardBody>
        {hasNoBalanceDue ? (
          /* Zero or Credit Balance Message */
          <div className="space-y-5">
            <div className="bg-success/10 border border-success/20 rounded-md px-4 py-3">
              <div className="flex items-start gap-3">
                <svg
                  className="h-5 w-5 text-success flex-shrink-0 mt-0.5"
                  xmlns="http://www.w3.org/2000/svg"
                  viewBox="0 0 20 20"
                  fill="currentColor"
                  aria-hidden="true"
                >
                  <path
                    fillRule="evenodd"
                    d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                    clipRule="evenodd"
                  />
                </svg>
                <p className="text-sm text-text">
                  You have a zero balance or a credit balance. No payment is due at this time.
                </p>
              </div>
            </div>

            {/* Back Button */}
            {onBack && (
              <div className="pt-4">
                <Button variant="ghost" onClick={onBack}>
                  &larr; Back to Login
                </Button>
              </div>
            )}
          </div>
        ) : (
          /* Normal Payment Form */
          <form onSubmit={handleSubmit(onSubmit)} className="space-y-5">
            {/* Quick Payment Buttons */}
            <div className="flex flex-wrap gap-3">
              {minimumPayment > 0 && minimumPayment < balance && (
                <Button type="button" variant="secondary" size="sm" onClick={handlePayMinimum}>
                  Pay Minimum ({formatCurrency(minimumPayment)})
                </Button>
              )}
              <Button type="button" variant="secondary" size="sm" onClick={handlePayInFull}>
                Pay in Full ({formatCurrency(balance)})
              </Button>
            </div>

            {/* Amount Input */}
            <div>
              <Input
                {...register('amount')}
                type="number"
                step="0.01"
                min="0"
                max={balance}
                label="Amount to Pay"
                placeholder="0.00"
                error={errors.amount?.message as string}
                required
              />
              <p className="text-xs text-text-muted mt-1">
                Enter the amount you wish to pay (max: {formatCurrency(balance)})
              </p>
            </div>

            {/* Full Payment Info */}
            {showFullPaymentInfo && (
              <div className="bg-success/10 border border-success/20 rounded-md px-4 py-3">
                <div className="flex items-start gap-3">
                  <svg
                    className="h-5 w-5 text-success flex-shrink-0 mt-0.5"
                    xmlns="http://www.w3.org/2000/svg"
                    viewBox="0 0 20 20"
                    fill="currentColor"
                    aria-hidden="true"
                  >
                    <path
                      fillRule="evenodd"
                      d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                      clipRule="evenodd"
                    />
                  </svg>
                  <p className="text-sm text-text">
                    You're paying your balance in full. Your account will be paid off after this
                    payment is processed.
                  </p>
                </div>
              </div>
            )}

            {/* Important Notes */}
            <div className="bg-surface rounded-md px-4 py-3 text-sm text-text-muted space-y-2">
              <p className="font-medium text-text">Important:</p>
              <ul className="list-disc list-inside space-y-1 ml-1">
                <li>Payments are processed immediately</li>
                <li>A confirmation email will be sent after payment</li>
                <li>Allow 2-3 business days for payment to reflect on your account</li>
              </ul>
            </div>

            {/* Action Buttons */}
            <div className="flex items-center justify-between pt-4 border-t border-border">
              {onBack ? (
                <Button type="button" variant="ghost" onClick={onBack}>
                  &larr; Back to Login
                </Button>
              ) : (
                <div />
              )}
              <Button type="submit" variant="primary">
                Next &rarr;
              </Button>
            </div>
          </form>
        )}
      </CardBody>
    </Card>
  );
}
